/**
 * Centralized Game State Manager
 *
 * Single source of truth for game/map state structure and socket payload shaping.
 *
 * ## Adding a new synced field (e.g. gridShapes)
 * 1. Add a default on `DEFAULT_MAP_STATE` (only place the canonical default lives).
 * 2. `GAME_STATE_KEYS` is derived automatically from that object (minus id/name).
 * 3. Server: extend `applyGameStatePatchToRoom` in server.js when adding a persisted field.
 *    Client sync uses `patchGameState` → `gameStatePatched`. The same event may include `permissions`
 *    (room lobby) alongside `patch` when saving room settings.
 * 4. If the field is cleared by “Clear grid”, add its key to `CLEAR_GRID_RESET_TO_DEFAULT_KEYS`.
 * 5. UI: read/write via context / hooks.
 *
 * ## Session I/O (immer reducer + `dispatchGameState`)
 * - **`hydrateSessionFromSource(dispatch, source)`** — map, join response, reconnect payload, etc.
 *   Fills missing keys from defaults, then dispatches one patch. Use for “load this map / snapshot”.
 * - **`patchSessionState(dispatch, partial)`** — only keys present are applied (same as `applyGameStateDispatch`).
 *   Use for `gameStatePatched` and other incremental updates.
 * Avoid `extractGameState` + `applyGameStateUpdates` at call sites when you have `dispatch`; those are for
 * legacy setter targets and internal helpers.
 *
 * Optional: if the React setter name is not `set` + PascalCase(key), add
 * `GAME_SESSION_SETTER_NAME_OVERRIDES` (see `battlemapId` → `setSelectedBattlemapId`).
 */

import { produce, current } from 'immer';

/**
 * Default map structure — add new persisted / playable fields here only.
 */
export const DEFAULT_MAP_STATE = {
  id: 'default',
  name: 'Main Map',
  gridWidth: 20,
  gridHeight: 20,
  gridColors: {},
  gridWalls: {},
  gridObjects: {},
  gridObjectInstances: {},
  coins: [],
  initiativeOrder: [],
  battlemapId: 'none'
};

const MAP_DOCUMENT_META_KEYS = new Set(['id', 'name']);

/**
 * All keys that belong to full map state.
 */
export const MAP_STATE_KEYS = Object.keys(DEFAULT_MAP_STATE);

/**
 * Playable state keys (everything on the map document except id/name).
 */
export const GAME_STATE_KEYS = MAP_STATE_KEYS.filter((k) => !MAP_DOCUMENT_META_KEYS.has(k));

function deepCloneGameValue(val) {
  if (val === null || val === undefined) return val;
  if (typeof val !== 'object') return val;
  return JSON.parse(JSON.stringify(val));
}

/** useMapManager `getInitialState` field: gridColors → initialGridColors */
function gameStateKeyToInitialStateKey(key) {
  return `initial${key.charAt(0).toUpperCase()}${key.slice(1)}`;
}

/**
 * Build `initialCoins`, `initialGridWidth`, … from any object carrying game state (e.g. a map).
 */
export function mapManagerInitFieldsFromSource(source) {
  const extracted = extractGameState(source);
  const out = {};
  for (const key of GAME_STATE_KEYS) {
    out[gameStateKeyToInitialStateKey(key)] = extracted[key];
  }
  return out;
}

const GAME_SESSION_SETTER_NAME_OVERRIDES = {
  battlemapId: 'setSelectedBattlemapId',
};

function gameStateKeyToSetterName(key) {
  return GAME_SESSION_SETTER_NAME_OVERRIDES[key] || `set${key.charAt(0).toUpperCase()}${key.slice(1)}`;
}

/**
 * Keys reset to `DEFAULT_MAP_STATE` when the user clears the grid (tokens/terrain/objects).
 * Omit gridWidth/gridHeight and non-layer fields you want to keep.
 */
const CLEAR_GRID_RESET_TO_DEFAULT_KEYS = [
  'gridColors',
  'gridWalls',
  'gridObjects',
  'gridObjectInstances',
];

/**
 * Create a new map with default values.
 */
export function createDefaultMap(overrides = {}) {
  return {
    ...DEFAULT_MAP_STATE,
    ...overrides,
    id: overrides.id || `map-${Date.now()}`,
    name: overrides.name || 'New Map'
  };
}

/**
 * Extract game state from an object that may contain map metadata.
 */
export function extractGameState(source) {
  const state = {};
  GAME_STATE_KEYS.forEach((key) => {
    state[key] = source?.[key] ?? DEFAULT_MAP_STATE[key];
  });
  return state;
}

/**
 * Build complete map object from id, name and game state.
 */
export function createMapFromState(id, name, gameState) {
  return {
    id,
    name,
    ...extractGameState(gameState)
  };
}

/**
 * Merge partial game state fields into an existing map.
 */
export function mergeGameStateIntoMap(map, gameState) {
  const updated = { ...map };
  GAME_STATE_KEYS.forEach((key) => {
    if (gameState[key] !== undefined) {
      updated[key] = gameState[key];
    }
  });
  return updated;
}

/**
 * Ensure missing map fields are filled with defaults.
 */
export function validateAndFillMap(map) {
  const validated = { ...DEFAULT_MAP_STATE };

  Object.keys(map || {}).forEach((key) => {
    if (map[key] !== undefined) {
      validated[key] = map[key];
    }
  });

  return validated;
}

/**
 * Convert map object into initial React state payload (for map editor / tooling).
 */
export function createInitialStateFromMap(map) {
  const validatedMap = validateAndFillMap(map);
  const gameState = extractGameState(validatedMap);
  const out = {};
  for (const key of GAME_STATE_KEYS) {
    out[gameStateKeyToInitialStateKey(key)] = gameState[key];
  }
  return out;
}

/**
 * Create complete map object from current React state-like object.
 */
export function createMapFromReactState(id, name, state) {
  return createMapFromState(id, name, extractGameState(state));
}

/**
 * Deep clone map object for duplication.
 */
export function cloneMap(map, newId, newName) {
  const safeMap = validateAndFillMap(map);
  const extracted = extractGameState(safeMap);
  const result = {
    id: newId,
    name: newName || `${safeMap.name} (Copy)`,
  };
  for (const key of GAME_STATE_KEYS) {
    result[key] = deepCloneGameValue(extracted[key]);
  }
  if (result.battlemapId === undefined || result.battlemapId === null) {
    result.battlemapId = 'none';
  }
  return result;
}

/**
 * Normalize full map for socket payloads.
 */
export function prepareMapForSocket(map) {
  const validated = validateAndFillMap(map);
  return {
    id: validated.id,
    name: validated.name,
    ...extractGameState(validated)
  };
}

/**
 * Normalize game state payload for socket payloads.
 */
export function prepareGameStateForSocket(gameState) {
  return extractGameState(gameState);
}

/** Action types for useReducer + immer game session state (see gameSessionReducer). */
export const GAME_SESSION_PATCH = 'gameSession/patch';
export const GAME_SESSION_PATCH_FN = 'gameSession/patchFn';

/**
 * Reducer for extracted game state (GAME_STATE_KEYS only). Use with useReducer + immer.
 */
export function gameSessionReducer(state, action) {
  return produce(state, (draft) => {
    switch (action.type) {
      case GAME_SESSION_PATCH: {
        const p = action.payload || {};
        for (const key of GAME_STATE_KEYS) {
          if (p[key] !== undefined) {
            draft[key] = p[key];
          }
        }
        break;
      }
      case GAME_SESSION_PATCH_FN:
        if (action.fn) {
          action.fn(draft);
        }
        break;
      default:
        break;
    }
  });
}

/**
 * Stable setters that dispatch immer-backed patches (supports functional updates).
 */
export function createGameSessionDispatchSetters(dispatch) {
  const out = {};
  for (const key of GAME_STATE_KEYS) {
    const setterName = gameStateKeyToSetterName(key);
    out[setterName] = (update) => {
      if (typeof update === 'function') {
        dispatch({
          type: GAME_SESSION_PATCH_FN,
          fn: (draft) => {
            const prev = current(draft)[key];
            draft[key] = update(prev);
          },
        });
      } else {
        dispatch({ type: GAME_SESSION_PATCH, payload: { [key]: update } });
      }
    };
  }
  return out;
}

/**
 * Deep-clone extracted game state for persisting snapshots into the maps array / storage.
 */
export function cloneGameStateSnapshot(source) {
  const extracted = extractGameState(source);
  const out = {};
  for (const key of GAME_STATE_KEYS) {
    out[key] = deepCloneGameValue(extracted[key]);
  }
  return out;
}

/**
 * Payload to hydrate game session state from useMapManager getInitialState().
 */
export function gameStatePatchFromMapManagerInit(initialState) {
  if (!initialState) return null;
  const patch = {};
  for (const key of GAME_STATE_KEYS) {
    const ik = gameStateKeyToInitialStateKey(key);
    if (initialState[ik] !== undefined) {
      patch[key] = initialState[ik];
    }
  }
  return patch;
}

/**
 * Local state when clearing the grid (terrain, walls, objects, tokens off grid).
 */
export function buildClearGridGameState(coins, initiativeOrder) {
  const clearedCoins = coins.map((coin) => ({
    ...coin,
    removed: true,
    x: undefined,
    y: undefined,
  }));
  const clearedInitiative = initiativeOrder.map((coin) => ({
    ...coin,
    removed: true,
    x: undefined,
    y: undefined,
  }));
  const out = {
    coins: clearedCoins,
    initiativeOrder: clearedInitiative,
    battlemapId: 'none',
  };
  for (const key of CLEAR_GRID_RESET_TO_DEFAULT_KEYS) {
    out[key] = deepCloneGameValue(DEFAULT_MAP_STATE[key]);
  }
  return out;
}

/**
 * Check whether the user is currently editing the active map.
 */
export function isOnActiveMap(roomId, viewingMapId, currentMapId) {
  return Boolean(roomId && viewingMapId === currentMapId);
}

/**
 * Sync a partial game-state patch to other clients (single `gameStatePatched` event).
 */
export function syncActiveMapState({
  roomId,
  viewingMapId,
  currentMapId,
  socketHook,
  patch,
}) {
  if (!isOnActiveMap(roomId, viewingMapId, currentMapId) || !socketHook || !patch) {
    return;
  }
  if (socketHook.patchGameState) {
    socketHook.patchGameState(roomId, viewingMapId, patch);
  }
}

/**
 * Apply only defined `GAME_STATE_KEYS` from `partialState` (incremental patch).
 */
export function applyGameStateDispatch(dispatch, partialState) {
  if (!dispatch || !partialState) return;
  const payload = {};
  for (const key of GAME_STATE_KEYS) {
    if (partialState[key] !== undefined) {
      payload[key] = partialState[key];
    }
  }
  if (Object.keys(payload).length > 0) {
    dispatch({ type: GAME_SESSION_PATCH, payload });
  }
}

/** Readable alias for `applyGameStateDispatch` (socket patches, local merges). */
export const patchSessionState = applyGameStateDispatch;

/**
 * Normalize `source` (map row, API payload, …) and replace the whole session game slice.
 * Do not use for raw incremental patches — use `patchSessionState` instead.
 */
export function hydrateSessionFromSource(dispatch, source) {
  if (!dispatch || source == null) return;
  applyGameStateDispatch(dispatch, extractGameState(source));
}

/**
 * Apply game state to provided React setters, or to dispatchGameState when present.
 * Prefer `hydrateSessionFromSource` / `patchSessionState` when the target is `dispatchGameState` only.
 */
export function applyGameStateUpdates(gameState, target) {
  if (target?.dispatchGameState) {
    applyGameStateDispatch(target.dispatchGameState, gameState);
    return;
  }

  for (const key of GAME_STATE_KEYS) {
    if (gameState[key] === undefined) continue;
    const setterName = gameStateKeyToSetterName(key);
    const setter = target[setterName];
    if (!setter) continue;

    let value = gameState[key];
    if (key === 'gridWalls' || key === 'gridObjects' || key === 'gridObjectInstances' || key === 'gridColors') {
      value = value || {};
    } else if (key === 'coins' || key === 'initiativeOrder') {
      value = value || [];
    } else if (key === 'battlemapId') {
      value = value || 'none';
    }
    setter(value);
  }
}

function gameStateFieldEqual(key, a, b) {
  const va = a[key];
  const vb = b[key];
  if (typeof va === 'object' && va !== null && typeof vb === 'object' && vb !== null) {
    return JSON.stringify(va) === JSON.stringify(vb);
  }
  return va === vb;
}

/**
 * Compare map game state against current state object.
 */
export function hasMapStateChanged(map, currentState) {
  const mapState = extractGameState(map);
  const current = extractGameState(currentState);
  return GAME_STATE_KEYS.some((key) => !gameStateFieldEqual(key, mapState, current));
}

/**
 * Apply room/join style response payload into local React state.
 * @param {object} ctx — `{ dispatchGameState, setRoomCreator?, setInitiativeOrder?, setPermissions? }`
 */
export function applyRoomStateFromResponse(response, ctx = {}) {
  if (ctx.dispatchGameState) {
    hydrateSessionFromSource(ctx.dispatchGameState, response);
  } else {
    applyGameStateUpdates(extractGameState(response), ctx);
  }

  if (ctx.setRoomCreator) {
    ctx.setRoomCreator(response.creator || response.creatorName || '');
  }
  if (response.initiativeOrder !== undefined && ctx.setInitiativeOrder) {
    ctx.setInitiativeOrder(response.initiativeOrder || []);
  }
  if (response.permissions && ctx.setPermissions) {
    ctx.setPermissions(response.permissions);
  }
}

/**
 * Build initial createRoom payload from current UI state.
 * `roomGameState` should include all `GAME_STATE_KEYS` you care about (e.g. full game context slice).
 */
export function buildCreateRoomPayload({
  width,
  height,
  userName,
  permissions,
  viewingMapId,
  currentMapId,
  roomGameState,
  maps,
}) {
  const activeMapSnapshot = createMapFromReactState(
    viewingMapId || currentMapId || 'default',
    'Active Map',
    {
      ...roomGameState,
      gridWidth: width,
      gridHeight: height,
      battlemapId: roomGameState.selectedBattlemapId ?? roomGameState.battlemapId ?? 'none',
    }
  );

  return {
    ...prepareMapForSocket(activeMapSnapshot),
    userName,
    permissions,
    maps: (maps || []).map((map) => prepareMapForSocket(map)),
    currentMapId,
  };
}

/**
 * Orchestrate settings save behavior across local + socket-backed game states.
 * Caller owns modal / isCreatingRoom UI state (close after await).
 * @param {object} ctx — session I/O: roomId, currentMapId, socketHook, dispatchGameState, setPermissions, setUserName, setRoomId, setRoomCreator, getRoomGameStateForPayload
 * @param {object} params — width, height, newPermissions, name?, addPresenter?, isCreatingRoom?
 */
export async function saveSettingsFlow(ctx, params) {
  const {
    width,
    height,
    newPermissions,
    name,
    addPresenter = false,
    isCreatingRoom = false,
  } = params;

  const {
    roomId,
    currentMapId,
    socketHook,
    dispatchGameState,
    setPermissions,
    setUserName,
    setRoomId,
    setRoomCreator,
    getRoomGameStateForPayload,
  } = ctx;

  const applyLocalSettings = () => {
    dispatchGameState({ type: GAME_SESSION_PATCH, payload: { gridWidth: width, gridHeight: height } });
    setPermissions(newPermissions);
  };

  if (roomId) {
    const mapIdForPatch =
      currentMapId ?? getRoomGameStateForPayload?.()?.currentMapId;
    if (mapIdForPatch) {
      socketHook.patchGameState(
        roomId,
        mapIdForPatch,
        { gridWidth: width, gridHeight: height },
        { permissions: newPermissions }
      );
    }
    applyLocalSettings();
    return { mode: 'room-settings-updated' };
  }

  if (name && isCreatingRoom) {
    setUserName(name);
    const roomGameState = getRoomGameStateForPayload();
    const initialData = buildCreateRoomPayload({
      width,
      height,
      userName: name,
      permissions: newPermissions,
      viewingMapId: roomGameState.viewingMapId,
      currentMapId: roomGameState.currentMapId,
      roomGameState,
      maps: roomGameState.maps,
    });

    const { roomId: newRoomId, creator } = await socketHook.createRoom(initialData);
    setRoomId(newRoomId);
    setRoomCreator(creator);
    applyLocalSettings();
    socketHook.addUser(name);

    if (addPresenter) {
      const presenterUrl = `${window.location.origin}?room=${newRoomId}&autoJoin=true&playerName=${encodeURIComponent('Presenter Player')}&isPresenter=true`;
      window.open(presenterUrl, '_blank');
    }

    return { mode: 'room-created', roomId: newRoomId };
  }

  applyLocalSettings();
  return { mode: 'local-settings-updated' };
}

export const DEFAULT_ROOM_PERMISSIONS = {
  allowCharacterManagement: true,
  allowEnemyManagement: false,
  allowPainting: false,
  allowEnemyHealthView: false,
};

/** Persisted map list + session snapshot (must match useMapManager). */
export const GRID_AND_GLORY_LOCAL_STORAGE_KEY = 'gridAndGloryState';

function isPresenterBrowsingMode() {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('isPresenter') === 'true';
  } catch (e) {
    return false;
  }
}

/** Remove persisted maps/session so room data cannot leak after disconnect or room delete. */
export function clearPersistedGridAndGloryState() {
  if (typeof window === 'undefined' || isPresenterBrowsingMode()) return;
  try {
    localStorage.removeItem(GRID_AND_GLORY_LOCAL_STORAGE_KEY);
  } catch (e) {
    console.error('Error clearing gridAndGloryState from localStorage:', e);
  }
}

/**
 * Reset in-memory maps + grid session to a single empty default map and clear persisted snapshot.
 * Call when leaving a room or when the room is deleted server-side.
 * @param {object} ctx — dispatchGameState, setMaps, setCurrentMapId, setViewingMapId, optional cancelPendingMapLocalStorageSave
 */
export function resetClientMapsAndSessionAfterLeavingRoom(ctx) {
  const {
    dispatchGameState,
    setMaps,
    setCurrentMapId,
    setViewingMapId,
    cancelPendingMapLocalStorageSave,
  } = ctx;
  cancelPendingMapLocalStorageSave?.();
  clearPersistedGridAndGloryState();
  const defaultMap = createDefaultMap({ id: 'default', name: 'Main Map' });
  setMaps([defaultMap]);
  setCurrentMapId('default');
  setViewingMapId('default');
  hydrateSessionFromSource(dispatchGameState, defaultMap);
}

/**
 * Orchestrate leave-room behavior and local state reset.
 * Caller closes leave-confirm / joining modals if needed.
 * @param {object} ctx — roomId, userName, isRoomCreator, socketHook, dispatchGameState, setMaps, setCurrentMapId, setViewingMapId, cancelPendingMapLocalStorageSave, setRoomId, setUserName, setRoomCreator, setPermissions, setConnectedUsers, navigate
 */
export function leaveRoomFlow(ctx) {
  const {
    roomId,
    userName,
    isRoomCreator,
    socketHook,
    dispatchGameState,
    setMaps,
    setCurrentMapId,
    setViewingMapId,
    cancelPendingMapLocalStorageSave,
    setRoomId,
    setUserName,
    setRoomCreator,
    setPermissions,
    setConnectedUsers,
    navigate,
  } = ctx;

  socketHook.socket.emit('leaveRoom', { roomId, userName });
  socketHook.leaveRoom();

  if (!isRoomCreator) {
    resetClientMapsAndSessionAfterLeavingRoom({
      dispatchGameState,
      setMaps,
      setCurrentMapId,
      setViewingMapId,
      cancelPendingMapLocalStorageSave,
    });
  }

  setRoomId(null);
  setUserName('');
  setRoomCreator('');
  setPermissions({ ...DEFAULT_ROOM_PERMISSIONS });
  setConnectedUsers([]);
  navigate('/');
}

/**
 * Save map editor changes for create/edit modes.
 * @param {object} ctx — roomId, socketHook, maps, setMaps, currentMapId, viewingMapId, onViewMap, onSelectBattlemap, generateMapId
 */
export async function saveMapEditorFlow(ctx, { mapEditorMode, mapData, editingMap, storageKey = 'gridAndGloryState' }) {
  const {
    roomId,
    socketHook,
    maps,
    setMaps,
    currentMapId,
    viewingMapId,
    onViewMap,
    onSelectBattlemap,
    generateMapId,
  } = ctx;
  if (mapEditorMode === 'create') {
    if (roomId) {
      await socketHook.createMap(
        roomId,
        prepareMapForSocket(
          createDefaultMap({
            name: mapData.name,
            gridWidth: mapData.gridWidth,
            gridHeight: mapData.gridHeight,
            battlemapId: mapData.battlemapId
          })
        )
      );
      return;
    }

    const newMapId = generateMapId();
    const newMap = createDefaultMap({
      id: newMapId,
      name: mapData.name,
      gridWidth: mapData.gridWidth,
      gridHeight: mapData.gridHeight,
      battlemapId: mapData.battlemapId
    });
    const updatedMaps = [...maps, newMap];
    setMaps(updatedMaps);
    localStorage.setItem(storageKey, JSON.stringify({
      currentMapId,
      viewingMapId: newMapId,
      maps: updatedMaps
    }));
    onViewMap(newMapId);
    return;
  }

  if (mapEditorMode === 'edit' && editingMap) {
    if (roomId) {
      if (mapData.name !== editingMap.name) {
        await socketHook.renameMap(roomId, editingMap.id, mapData.name);
      }
      socketHook.patchGameState(roomId, editingMap.id, {
        battlemapId: mapData.battlemapId,
        gridWidth: mapData.gridWidth,
        gridHeight: mapData.gridHeight,
      });
    }

    setMaps((prevMaps) => prevMaps.map((map) => (
      map.id === editingMap.id
        ? {
            ...map,
            name: mapData.name,
            gridWidth: mapData.gridWidth,
            gridHeight: mapData.gridHeight,
            battlemapId: mapData.battlemapId
          }
        : map
    )));

    if (editingMap.id === viewingMapId) {
      onSelectBattlemap(mapData.battlemapId, {
        emitUpdate: false,
        gridWidthOverride: mapData.gridWidth,
        gridHeightOverride: mapData.gridHeight,
      });
    }
  }
}

/**
 * Duplicate map for room and local modes.
 * @param {object} ctx — same map/session slice as saveMapEditorFlow
 */
export async function duplicateMapFlow(ctx, mapId, storageKey = 'gridAndGloryState') {
  const {
    roomId,
    socketHook,
    maps,
    currentMapId,
    setMaps,
    onViewMap,
    generateMapId,
  } = ctx;

  if (roomId) {
    await socketHook.duplicateMap(roomId, mapId);
    return;
  }

  const originalMap = maps.find((m) => m.id === mapId);
  if (!originalMap) return;

  const newMapId = generateMapId();
  const newMap = cloneMap(originalMap, newMapId, `${originalMap.name} (Copy)`);
  const updatedMaps = [...maps, newMap];
  setMaps(updatedMaps);
  localStorage.setItem(storageKey, JSON.stringify({
    currentMapId,
    viewingMapId: newMapId,
    maps: updatedMaps
  }));
  onViewMap(newMapId);
}

/**
 * Delete map for room and local modes.
 * @param {object} ctx — maps, setMaps, currentMapId, viewingMapId, roomId, socketHook, onActivateMap, onViewMap
 */
export async function deleteMapFlow(ctx, selectedMapId) {
  if (!selectedMapId) return;

  const {
    roomId,
    socketHook,
    maps,
    currentMapId,
    viewingMapId,
    setMaps,
    onActivateMap,
    onViewMap,
  } = ctx;

  if (roomId) {
    await socketHook.deleteMap(roomId, selectedMapId);
    return;
  }

  if (maps.length <= 1) {
    throw new Error('Cannot delete the only map');
  }

  if (currentMapId === selectedMapId || viewingMapId === selectedMapId) {
    const otherMap = maps.find((m) => m.id !== selectedMapId);
    if (!otherMap) return;
    const mapToSwitchTo = otherMap.id;

    setTimeout(() => {
      setMaps((prevMaps) => prevMaps.filter((map) => map.id !== selectedMapId));
      if (currentMapId === selectedMapId) {
        onActivateMap(mapToSwitchTo);
      } else if (viewingMapId === selectedMapId) {
        onViewMap(mapToSwitchTo);
      }
    }, 0);
    return;
  }

  setMaps((prevMaps) => prevMaps.filter((map) => map.id !== selectedMapId));
}
